/**
 * Acquisition attribution the vendor cannot see for itself (PIX-556).
 *
 * PostHog already captures campaign parameters and referrer as first-touch
 * person properties, which persist and stay queryable on every later event -
 * so channel attribution is largely free and nothing here duplicates it.
 *
 * What it cannot see is our own codes: the friend referral code and the
 * partnership code, which arrive as query parameters and are stashed in
 * browser storage so they survive a visitor wandering the site before
 * signing up. Capturing them as person properties is what makes "do referred
 * students convert differently" answerable.
 *
 * Pure apart from storage reads, and every read is guarded - a browser with
 * storage disabled (private mode, strict settings) must not break the page.
 */
import { ATTRIBUTION_PROPS } from './events.js';

const REFERRAL_STORAGE_KEY = 'smp_ref_code';
const PARTNERSHIP_STORAGE_KEY = 'smp_partnership_code';

/** Read a key from localStorage, tolerating storage being unavailable. */
function readStorage(key) {
  try {
    return localStorage.getItem(key) || '';
  } catch (_) {
    return '';
  }
}

/**
 * Resolve attribution codes for the current visit.
 *
 * Query parameters win over storage: a student arriving on a fresh referral
 * link should be attributed to that link, not to whatever they picked up on
 * a previous visit.
 *
 * @param {object} query  the current route's query object
 */
export function resolveAttribution(query = {}) {
  const fromQuery = (name) => (query?.[name] ?? '').toString().trim();

  const referral =
    fromQuery('ref').toUpperCase() || readStorage(REFERRAL_STORAGE_KEY);
  const partnership =
    fromQuery('voucher') || readStorage(PARTNERSHIP_STORAGE_KEY);

  const attribution = {};
  if (referral) attribution[ATTRIBUTION_PROPS.REFERRAL_CODE] = referral;
  if (partnership)
    attribution[ATTRIBUTION_PROPS.PARTNERSHIP_CODE] = partnership;
  return attribution;
}
